import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceSampleRange } from '@utils/types/organization/voice-profile.ts';
import {
  formatRangeClock,
  MAX_SAMPLE_RANGES,
  rangeDurationMs,
  TimelineLabel
} from '@utils/editors/voice-sample-ranges.ts';

interface Props {
  ranges: VoiceSampleRange[];
  onChange: (ranges: VoiceSampleRange[]) => void;
  /** The label's own source spans, offered as passages to combine. */
  label?: TimelineLabel;
  disabled?: boolean;
}

/** Adjust and combine the passages that make up a voice sample.
 *
 * Starts from the backend's clean-speech plan, so the common case needs no
 * editing at all. Beyond that the editor can trim a passage's seconds or add
 * another turn from the same detected speaker — never from a different one,
 * which is why the candidates come from that label's own timeline spans. */
export const VoiceSampleRangeEditor = ({
  ranges,
  onChange,
  label,
  disabled = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const update = (index: number, patch: Partial<VoiceSampleRange>) => {
    onChange(
      ranges.map((range, i) => (i === index ? { ...range, ...patch } : range))
    );
  };

  const remove = (index: number) => {
    onChange(ranges.filter((_, i) => i !== index));
  };

  // Spans of this same label that aren't already covered by a chosen range.
  const candidates = (label?.spans ?? []).filter(
    span =>
      !ranges.some(
        range => range.start_ms < span.stop_ms && span.start_ms < range.stop_ms
      )
  );

  const atRangeLimit = ranges.length >= MAX_SAMPLE_RANGES;

  // Seconds are the unit the backend's 10–60s rule is expressed in, so the
  // inputs use seconds rather than making the user think in milliseconds.
  const secondsValue = (ms: number) => Math.round(ms / 100) / 10;

  return (
    <div className="space-y-2">
      <Text as="p" size="xs" tone="muted">
        {t('voice-selected-passages')}
      </Text>

      {ranges.length === 0 && (
        <Text as="p" size="sm" tone="muted">
          {t('voice-no-passages-selected')}
        </Text>
      )}

      {/* With up to 20 passages this list could push every control below it
          off-screen; cap the height and scroll inside the editor itself. */}
      <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
        {ranges.map((range, index) => (
          <li
            key={`${range.start_ms}-${range.stop_ms}-${index}`}
            className="flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5"
            style={{ borderColor: getColour('border') }}
          >
            {/* Top row: readable range summary + delete. Stays on one line
                because both are compact, and `min-w-0` lets the summary
                truncate rather than push the delete button off-screen. */}
            <span className="flex min-w-0 flex-1 items-center justify-between gap-2 text-sm">
              <span className="truncate">
                {formatRangeClock(range.start_ms)} –{' '}
                {formatRangeClock(range.stop_ms)}
                <Text as="span" size="xs" tone="muted">
                  {' '}
                  ({secondsValue(rangeDurationMs(range))}s)
                </Text>
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={t('voice-remove-passage')}
                className="shrink-0 cursor-pointer rounded p-1 hover:opacity-70 disabled:opacity-40"
              >
                <Trash2 size={14} />
              </button>
            </span>

            {/* Bottom row: Start/End inputs. Wraps underneath on narrow
                screens, inlines next to the summary on wider screens — the
                row's `flex-wrap` is what makes both layouts work from the
                same markup. */}
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <label className="flex items-center gap-1 text-xs">
                <span className="sr-only">
                  {t('voice-passage-start-seconds')}
                </span>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={secondsValue(range.start_ms)}
                  disabled={disabled}
                  aria-label={t('voice-passage-start-seconds')}
                  onChange={event =>
                    update(index, {
                      start_ms: Math.max(
                        0,
                        Math.round(Number(event.target.value) * 1000)
                      )
                    })
                  }
                  className="w-20 rounded border px-1 py-0.5"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('secondLayer'),
                    color: getColour('normalText')
                  }}
                />
              </label>

              <label className="flex items-center gap-1 text-xs">
                <span className="sr-only">
                  {t('voice-passage-stop-seconds')}
                </span>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={secondsValue(range.stop_ms)}
                  disabled={disabled}
                  aria-label={t('voice-passage-stop-seconds')}
                  onChange={event =>
                    update(index, {
                      stop_ms: Math.max(
                        0,
                        Math.round(Number(event.target.value) * 1000)
                      )
                    })
                  }
                  className="w-20 rounded border px-1 py-0.5"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('secondLayer'),
                    color: getColour('normalText')
                  }}
                />
              </label>
            </div>
          </li>
        ))}
      </ul>

      {candidates.length > 0 && !atRangeLimit && (
        <div className="space-y-1">
          <Text as="p" size="xs" tone="muted">
            {t('voice-add-passage-hint')}
          </Text>
          {/* Plain buttons rather than the Button atom: that atom hides its
              label below 1080px whenever an icon is present, and here the
              timestamp IS the content — without it these are two
              indistinguishable plus signs. */}
          <div className="flex flex-wrap gap-1">
            {candidates.slice(0, 8).map(span => {
              const text = `${formatRangeClock(
                span.start_ms
              )} – ${formatRangeClock(span.stop_ms)}`;
              return (
                <button
                  key={`${span.start_ms}-${span.stop_ms}`}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onChange([
                      ...ranges,
                      { start_ms: span.start_ms, stop_ms: span.stop_ms }
                    ])
                  }
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('secondLayer'),
                    color: getColour('normalText')
                  }}
                >
                  <Plus size={12} aria-hidden />
                  <span>{text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {atRangeLimit && (
        <Text as="p" size="xs" tone="muted">
          {t('voice-range-limit-reached', { max: MAX_SAMPLE_RANGES })}
        </Text>
      )}
    </div>
  );
};
